import { useRef, useState } from "react";
import { Button, CommonModal, Loader } from "@/common/components/ui";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import ContributionService from "@/services/FinEaseUser/APIs/ContributionService";
import ContributionPaymentStatusModal from "@/projects/NPS/Stepper/components/ContributionPaymentStatusModal";

const ContributionsPayModal = ({
  amount,
  registrationDetails,
}: {
  amount: number | null | undefined;
  registrationDetails: Registration | null | undefined;
}) => {
  const [upiId, setUpiId] = useState("");
  const [verifiedName, setVerifiedName] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [paypointzPaymentId, setPaypointzPaymentId] = useState<string | null>(
    null
  );

  const upiInputRef = useRef<HTMLInputElement | null>(null);

  const [qrLoading, setQrLoading] = useState(false);
  const [qrImage, setQrImage] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const [_polling, setPolling] = useState(false);
  //   const mobileNumber = registrationDetails?.initialDetails?.user?.phone_number;

  //   const localMobileNumber = localStorage.getItem("userMobile");

  const handleVerifyVPA = async () => {
    if (!upiId) {
      setError("Please enter UPI ID");

      // 🔥 Auto-focus when empty
      setTimeout(() => {
        upiInputRef.current?.focus();
      }, 50);

      return;
    }

    setIsVerifying(true);
    setError("");
    setVerifiedName("");

    try {
      const payload = { vpaAddress: upiId };
      const response = await ContributionService.ContributeValidateVPA(payload);

      if (response?.vpa_holder_name) {
        setVerifiedName(response.vpa_holder_name);
      } else {
        setError("Could not verify UPI ID");

        // 🔥 Focus again when incorrect
        setTimeout(() => {
          upiInputRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      console.error(err, "err");
      setError("Invalid UPI ID");

      // 🔥 Focus again when API error
      setTimeout(() => {
        upiInputRef.current?.focus();
      }, 50);
    } finally {
      setIsVerifying(false);
    }
  };
  const onClose = () => {
    setPolling(false);
    setPaymentSuccess(false);
    setPaymentFailed(false);
    setOpenStatusModal(false);
  };

  const sendUPIRequest = async () => {
    if (!verifiedName) {
      setError("Please verify UPI ID first");
      return;
    }

    setRequestLoading(true);

    try {
      const payload = {
        pranNumber: registrationDetails?.initialDetails?.pranNumber,
        vpaAddress: upiId,
        vpaHolderName: verifiedName,
        amountOfContribution: "1.00",
      };

      const res =
        await ContributionService.ContributeRequestUPICollection(payload);

      if (res?.success) {
        setPaypointzPaymentId(res?.paypointz_payment_id);
        setOpenStatusModal(true);
      }
    } catch (e) {
      console.error(e, "e");
      setError("Failed to send UPI request");
    } finally {
      setRequestLoading(false);
    }
  };

  const startPaymentStatusPolling = (paymentId: string) => {
    if (!paymentId) {
      return;
    }

    setPolling(true);

    const interval = setInterval(async () => {
      try {
        const res = await ContributionService.ContributeGetPaymentStatus(
          registrationDetails?.initialDetails?.pranNumber ?? ""
        );

        const status = res?.status?.toLowerCase();
        if (status === "success") {
          setPaymentSuccess(true);
        } else if (status === "failed") {
          setPaymentFailed(true);
        }
        if (status === "success" || status === "failed") {
          clearInterval(interval);
          setPolling(false);
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000); // Poll every 3 sec
  };

  const generateQR = async () => {
    setQrLoading(true);
    setError("");

    try {
      const payload = {
        pranNumber: registrationDetails?.initialDetails?.pranNumber,
        amountOfContribution: "1.00",
      };

      const res = await ContributionService.ContributeGenerateQR(payload);

      if (res?.image_url) {
        setPaypointzPaymentId(res?.paypointz_payment_id);
        // Remove prefix if API already added it
        const cleanedBase64 = res.image_url.replace(
          /^data:image\/png;base64,/,
          ""
        );

        // Set final usable src string
        setQrImage(`data:image/png;base64,${cleanedBase64}`);
        startPaymentStatusPolling(res?.paypointz_payment_id);
      } else {
        setError("Failed to generate QR");
      }
    } catch {
      setError("QR generation failed");
    } finally {
      setQrLoading(false);
    }
  };

  const isLoading = isVerifying || requestLoading || qrLoading;
  return (
    <div className="modal-animate w-full px-5 py-16">
      {isLoading && <Loader message="Processing your request..." />}
      <div className="grid grid-cols-1 items-stretch gap-8 md:grid-cols-2">
        {/* LEFT SIDE */}
        <div className="left-slide flex h-full flex-col">
          <h2 className="mb-6 text-center font-sans text-[22px] font-bold text-[#000]">
            Payment Details
          </h2>

          {/* Total Price */}
          <div className="mb-4 flex justify-between font-sans text-lg font-semibold">
            <span className="font-sans text-[#707070]">Total Price</span>
            <span className="font-sans text-[#2F363F]">₹{amount}</span>
          </div>

          {/* UPI ID */}
          <label className="mb-1 block font-sans text-[16px] font-semibold text-[#707070]">
            UPI ID
          </label>

          <div className="flex items-center gap-3">
            <input
              type="text"
              ref={upiInputRef}
              value={upiId}
              placeholder="maheshv@oksbi"
              onChange={e => {
                setUpiId(e.target.value);
                setVerifiedName("");
                setError("");
              }}
              onBlur={handleVerifyVPA}
              className="w-full rounded-md border px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]"
            />

            {/* Verify Button */}
            <Button
              onClick={handleVerifyVPA}
              className="h-[45px] min-w-[100px] cursor-pointer rounded-md bg-gradient-to-l from-[#66A0CD] to-[#1D2C56] font-sans text-[16px] font-semibold text-white"
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </Button>
          </div>

          {/* Verified Holder Name */}
          {verifiedName && (
            <div className="pop-in mt-2 flex items-center gap-2">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#66A0CD] text-xs text-white">
                ✓
              </span>
              <span className="font-sans text-sm font-semibold text-[#66A0CD]">
                {verifiedName}
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-shake mt-2 text-sm font-semibold text-red-500">
              {error}
            </div>
          )}

          {/* Send Request Button */}
          <Button
            onClick={sendUPIRequest}
            disabled={!!paypointzPaymentId || !!error || !verifiedName}
            className={`mt-4 h-[50px] w-full bg-gradient-to-l from-[#66A0CD] to-[#1D2C56] text-[16px] font-semibold text-white md:mt-auto ${paypointzPaymentId || error || !verifiedName ? "btn-disabled" : "cursor-pointer"}`}
          >
            {requestLoading ? "Processing..." : "Send UPI Request"}
          </Button>
        </div>

        {/* RIGHT SIDE SAME */}
        <div className="fade-scale flex flex-col items-center">
          <h2 className="mb-4 font-sans text-[20px] font-bold text-[#2F363F]">
            Scan with QR Code
          </h2>

          <div
            className={`relative flex h-[260px] w-[260px] items-center justify-center rounded-2xl ${!qrImage ? "border" : ""}`}
          >
            {qrLoading ? (
              <div className="h-full w-full animate-pulse rounded-xl bg-gray-200" />
            ) : !qrImage ? (
              <img
                src="/assets/qr-code.png"
                alt="qr-code"
                className="fade-scale opacity-60"
              />
            ) : (
              <img
                src={qrImage}
                alt="qr-code"
                className="fade-scale qr-glow h-full w-full rounded-xl object-cover"
              />
            )}

            {/* Generate QR Button */}
            {!qrImage && (
              <Button
                onClick={generateQR}
                disabled={!!paypointzPaymentId}
                className={`absolute left-1/2 top-1/2 h-[50px] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-md bg-gradient-to-l from-[#66A0CD] to-[#1D2C56] px-6 font-sans text-[16px] font-semibold text-white transition-opacity duration-500 ${qrLoading ? "opacity-50" : ""} ${paypointzPaymentId ? "btn-disabled" : "cursor-pointer"}`}
              >
                {qrLoading ? "Loading..." : "Generate Now"}
              </Button>
            )}
          </div>

          {/* <p className="mt-3 text-sm text-gray-600">{upiId}</p> */}
          <div className="mt-4 text-center font-sans font-semibold text-[#707070]">
            Scan And Pay with UPI
          </div>

          {/* Apps */}
          <div className="mt-2 flex flex-wrap justify-center gap-3 opacity-90 md:flex-nowrap md:justify-start">
            <img
              src="/assets/bhimup.png"
              alt="bhim"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
            <img
              src="/assets/upi.png"
              alt="upi"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 md:flex-nowrap md:justify-start">
            <img
              src="/assets/gp.png"
              alt="gpay"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
            <img
              src="/assets/phone-pay.png"
              alt="phonepe"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
            <img
              src="/assets/paytm.png"
              alt="paytm"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
            <img
              src="/assets/amazon.png"
              alt="amazonpay"
              className="h-[28px] w-auto object-contain md:h-[28px]"
            />
          </div>
        </div>
      </div>

      <CommonModal
        isOpen={openStatusModal}
        onClose={() => setOpenStatusModal(false)}
      >
        <ContributionPaymentStatusModal
          pranNumber={registrationDetails?.initialDetails?.pranNumber ?? ""}
          onClose={() => setOpenStatusModal(false)}
          amount={amount ?? 0}
        />
      </CommonModal>
      <CommonModal isOpen={paymentSuccess} onClose={onClose}>
        <div className="mx-auto w-full px-10 py-12 text-center font-sans sm:w-[500px]">
          <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#E4FFFB]">
            <img
              src="/assets/Checkmark Circle.svg"
              alt="success"
              className="h-[60px]"
            />
          </div>

          <h2 className="mt-4 text-[40px] font-extrabold">Congratulations</h2>
          <p className="mt-2 text-[16px] font-bold text-[#707070]">
            Payment Done
          </p>
          <p className="mt-2 text-[16px] font-bold text-[#707070]">
            Transaction Completed
          </p>

          <p className="mt-4 text-[16px] font-bold">
            Ref: {paypointzPaymentId}
          </p>

          <Button
            onClick={onClose}
            className="m-auto mt-6 h-[50px] rounded-[8px] bg-[#F5F5F5] px-6 font-bold"
          >
            Close
          </Button>
        </div>
      </CommonModal>

      <CommonModal isOpen={paymentFailed} onClose={onClose}>
        <div className="px-10 py-12 text-center font-sans sm:w-[500px]">
          <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#FFDEDE]">
            <img src="/assets/error.svg" alt="error" className="h-[60px]" />
          </div>

          <h2 className="mt-6 text-[32px] font-bold">Oops!</h2>

          <p className="mt-2 text-[18px] font-semibold text-[#707070]">
            Payment Failed
          </p>
          <p className="mt-2 text-[18px] font-semibold text-[#707070]">
            Transaction Failed
          </p>

          <p className="mt-4 text-[18px] font-bold">
            Ref: {paypointzPaymentId}
          </p>

          <Button
            onClick={onClose}
            className="mx-auto mt-8 h-[50px] rounded-md bg-[#F5F5F5] px-8 font-bold"
          >
            Close
          </Button>
        </div>
      </CommonModal>
    </div>
  );
};

export default ContributionsPayModal;
